import React, {useState, useEffect} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {RTL} from "../cache/CacheRtl";
import MeasurementCard from "./MeasurementCard";
import PersonalMeasurementDialog from "./client/measurements/PersonalMeasurementDialog";
import {addBulkMeasurements, getLatestMeasurements, getDefinitionsForTrainee} from "../services/BodyMeasurementsService";
import {getCurrentDate} from "../utils/formUtils";

// Fully definition-based: the company measurement catalog drives which cards appear
// (active defs only), keyed by bodyMeasurementDefinitionId. No enum, no custom-type manager,
// no US-Navy auto-calc (body-fat-circumference is now a normal manual measurement).
const AddBodyMeasurementModal = ({open, onClose, onSave, clientId}) => {
    const [measurementDate, setMeasurementDate] = useState(
        getCurrentDate()
    );

    const [definitions, setDefinitions] = useState([]);
    const [measurements, setMeasurements] = useState({});          // { defId: value }
    const [previousMeasurements, setPreviousMeasurements] = useState({}); // { defId: value }
    const [previousDates, setPreviousDates] = useState({});        // { defId: date }
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [personalDialogOpen, setPersonalDialogOpen] = useState(false);

    useEffect(() => {
        if (open && clientId) {
            fetchDefinitions();
            fetchPreviousMeasurements();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientId]);

    const fetchDefinitions = async () => {
        try {
            // Trainee-scoped: only this trainee's gender measurements, in their gender-grid order.
            const res = await getDefinitionsForTrainee(clientId);
            const defs = (res.data || [])
                // A computed measurement (נוסחת היקפים) is derived from other measurements — there is
                // nothing to type into it, and the server refuses a value for it. Offering the field
                // would be offering an action that cannot succeed.
                .filter(d => !d.computed)
                .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
            setDefinitions(defs);
        } catch (err) {
            console.error('Error fetching measurement definitions:', err);
            setDefinitions([]);
        }
    };

    const fetchPreviousMeasurements = async () => {
        setLoading(true);
        try {
            const response = await getLatestMeasurements(clientId);
            const prev = {};
            const dates = {};
            (response.data.measurements || []).forEach(m => {
                if (m.bodyMeasurementDefinitionId != null) {
                    prev[m.bodyMeasurementDefinitionId] = m.value?.toString() || '';
                    dates[m.bodyMeasurementDefinitionId] = m.measurementDate;
                }
            });
            setPreviousMeasurements(prev);
            setPreviousDates(dates);
        } catch (err) {
            console.error('Error fetching previous measurements:', err);
            setPreviousMeasurements({});
            setPreviousDates({});
        } finally {
            setLoading(false);
        }
    };

    const unitLabel = (code) => ({cm: 'ס״מ', kg: 'ק״ג', '%': '%'}[code] || code || 'ס״מ');

    const handleChange = (defId, value) => {
        setMeasurements(prev => ({...prev, [defId]: value}));
    };

    const handleSave = async () => {
        const filled = {};
        Object.entries(measurements).forEach(([defId, value]) => {
            if (value !== '' && value != null) {
                filled[defId] = parseFloat(value);
            }
        });

        if (Object.keys(filled).length === 0) {
            alert('אנא הזן לפחות מדידה אחת');
            return;
        }

        setSaving(true);
        try {
            await addBulkMeasurements(clientId, {measurements: filled, measurementDate});
            onSave();
            handleClose();
        } catch (error) {
            console.error('Error saving measurements:', error);
            alert('שגיאה בשמירת המדידות. אנא נסה שנית.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setMeasurements({});
        setMeasurementDate(getCurrentDate());
        setPreviousMeasurements({});
        setPreviousDates({});
        onClose();
    };

    return (
        <RTL>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
                    PaperProps={{sx: {borderRadius: "16px", direction: "rtl"}}}>
                <DialogContent sx={{padding: 0}}>
                    {/* Header */}
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "24px", borderBottom: "1px solid #E2E8F0"}}>
                        <Box sx={{width: 40}}/>
                        <Typography sx={{fontSize: "20px", fontWeight: 600, color: "#1E293B"}}>הוספת מדידת גוף</Typography>
                        <IconButton onClick={handleClose} sx={{color: "#64748B", '&:hover': {backgroundColor: "#F1F5F9"}}}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{padding: "24px"}}>
                        {/* Date picker + "add a personal measurement".
                            The personal-measurement button belongs UP HERE, with the date, because
                            it acts on the LIST below rather than on the values being entered — a
                            trainer who needs a measurement that isn't in the company catalog can
                            add it without scrolling past every card to find the control. */}
                        <Box sx={{
                            mb: 3, display: "flex", flexWrap: "wrap", gap: 2,
                            justifyContent: "space-between", alignItems: "flex-end",
                        }}>
                            <RTL>
                                <Button
                                    startIcon={<AddIcon/>}
                                    onClick={() => setPersonalDialogOpen(true)}
                                    disabled={loading || saving}
                                    sx={{
                                        textTransform: "none", borderRadius: "8px",
                                        color: "#5B9A8B", fontWeight: 600, fontSize: "14px",
                                        '&:hover': {backgroundColor: "#F0FDF9"}
                                    }}>
                                    הוספת מדד אישי
                                </Button>
                            </RTL>
                            <Box sx={{display: "flex", flexDirection: "column", alignItems: "flex-end"}}>
                                <Typography sx={{fontSize: "14px", fontWeight: 500, color: "#64748B", mb: 1}}>תאריך מדידה</Typography>
                                <TextField type="date" value={measurementDate}
                                           onChange={(e) => setMeasurementDate(e.target.value)}
                                           disabled={loading || saving} size="small"
                                           sx={{width: "200px", '& .MuiOutlinedInput-root': {borderRadius: "8px", backgroundColor: "#F8FAFC", '& fieldset': {borderColor: "#E2E8F0"}, '&:hover fieldset': {borderColor: "#CBD5E1"}}}}/>
                            </Box>
                        </Box>

                        {/* Measurement grid — one card per active company definition.
                            Uses flex `row-reverse` + wrap for reliable RTL flow (cards fill
                            RIGHT-to-left: order 0 = top-right). `direction: rtl` on a CSS grid is
                            unreliable here — stylis-plugin-rtl flips it back to ltr. Each card is
                            ~2 columns (calc for the gap). */}
                        <Box sx={{
                            display: "flex", flexWrap: "wrap", flexDirection: "row-reverse",
                            // Each card sizes to its OWN content. Without this the default
                            // `stretch` makes every item as tall as the tallest card on its flex
                            // line, and the `height: 100%` that used to sit on the card then
                            // resolved against that line — so a card whose neighbour wrapped to two
                            // title lines spilled over the row below it. That was the overlap.
                            alignItems: "flex-start",
                            gap: 2, mb: 3,
                        }}>
                            {definitions.map((def) => (
                                <Box key={def.id} data-testid={`measurement-cell-${def.id}`} sx={{
                                    // Two columns when there's room, one when there isn't. A fixed
                                    // 50% forced two cramped columns at every width, which is what
                                    // squeezed the card contents into each other on smaller screens.
                                    width: {xs: "100%", sm: "calc(50% - 8px)"},
                                    minWidth: 0,
                                }}>
                                    <MeasurementCard
                                        label={def.nameHeb || def.name}
                                        unit={unitLabel(def.unit)}
                                        value={measurements[def.id] || ''}
                                        previousValue={previousMeasurements[def.id]}
                                        previousMeasurementDate={previousDates[def.id]}
                                        onChange={(newValue) => handleChange(def.id, newValue)}
                                        disabled={loading || saving}
                                    />
                                </Box>
                            ))}
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{display: "flex", gap: 2, justifyContent: "center"}}>
                            <Button variant="outlined" onClick={handleClose} disabled={saving}
                                    sx={{borderColor: "#E2E8F0", color: "#64748B", textTransform: "none", borderRadius: "8px", padding: "10px 24px", fontWeight: 500, '&:hover': {borderColor: "#CBD5E1", backgroundColor: "#F8FAFC"}}}>
                                ביטול
                            </Button>
                            <Button variant="contained" onClick={handleSave} disabled={saving || loading}
                                    sx={{backgroundColor: "#5B9A8B", color: "white", textTransform: "none", borderRadius: "8px", padding: "10px 24px", fontWeight: 500, boxShadow: "none", '&:hover': {backgroundColor: "#0D9488", boxShadow: "none"}, '&:disabled': {backgroundColor: "#CBD5E1", color: "#94A3B8"}}}>
                                {saving ? 'שומר...' : 'שמור מדידות'}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Nested: create a personal measurement, then re-read the definitions so the new
                    card appears in the grid straight away (already scrolled into, ready to fill). */}
                {personalDialogOpen && (
                    <PersonalMeasurementDialog
                        open
                        traineeId={Number(clientId)}
                        item={null}
                        onClose={() => setPersonalDialogOpen(false)}
                        onSaved={() => {
                            setPersonalDialogOpen(false);
                            fetchDefinitions();
                        }}
                    />
                )}
            </Dialog>
        </RTL>
    );
};

export default AddBodyMeasurementModal;
